import React from 'react';
import './CooldownBlocks.css';

/**
 * CooldownBlocks - Visual cooldown/duration indicator with blocks
 * Shows filled blocks for remaining cooldown/duration, empty for available/expired
 * Each block contains "T" for "Turn"
 *
 * @param {number} cooldownMax - Maximum turns (0 = no cooldown, show ∞)
 * @param {number} cooldownCurrent - Current turns remaining (0 = ready/expired)
 * @param {boolean} isPlanned - If true, show as "will be on cooldown" after planning
 * @param {string} mode - 'cooldown' (default) or 'duration'
 *   - cooldown: For actions - filled=on cooldown (bad), empty=available (good)
 *   - duration: For effects - filled=remaining (good), empty=expired (neutral)
 */
const CooldownBlocks = ({ cooldownMax, cooldownCurrent, isPlanned = false, mode = 'cooldown' }) => {
  // No cooldown system - show infinity symbol
  if (!cooldownMax || cooldownMax <= 0) {
    return (
      <div className="cooldown-blocks cooldown-infinite" title="No cooldown">
        <span className="cooldown-infinity">∞</span>
      </div>
    );
  }

  // Calculate filled vs empty blocks
  let filledBlocks = cooldownCurrent || 0;
  let emptyBlocks = cooldownMax - filledBlocks;

  // If action is planned and ready, show it will go on cooldown (cooldown mode only)
  if (mode === 'cooldown' && isPlanned && filledBlocks === 0) {
    filledBlocks = cooldownMax;
    emptyBlocks = 0;
  }

  // Tooltip text based on mode
  const tooltipText = mode === 'duration'
    ? `${filledBlocks} turn${filledBlocks !== 1 ? 's' : ''} remaining`
    : isPlanned && cooldownCurrent === 0
      ? `Will be on cooldown for ${cooldownMax} turn${cooldownMax > 1 ? 's' : ''}`
      : `Cooldown: ${filledBlocks}/${cooldownMax} turns`;

  // CSS class for mode
  const modeClass = mode === 'duration' ? 'cooldown-duration' : '';

  return (
    <div className={`cooldown-blocks ${modeClass}`} title={tooltipText}>
      {mode === 'duration' ? (
        // Duration mode: filled blocks first (remaining), then empty (expired)
        <>
          {Array.from({ length: filledBlocks }).map((_, i) => (
            <div key={`filled-${i}`} className="cooldown-block cooldown-block-filled">
              <span className="cooldown-block-text">T</span>
            </div>
          ))}
          {Array.from({ length: emptyBlocks }).map((_, i) => (
            <div key={`empty-${i}`} className="cooldown-block cooldown-block-empty">
              <span className="cooldown-block-text">T</span>
            </div>
          ))}
        </>
      ) : (
        // Cooldown mode: empty blocks first (available), then filled (on cooldown)
        <>
          {Array.from({ length: emptyBlocks }).map((_, i) => (
            <div key={`empty-${i}`} className="cooldown-block cooldown-block-empty">
              <span className="cooldown-block-text">T</span>
            </div>
          ))}
          {Array.from({ length: filledBlocks }).map((_, i) => (
            <div
              key={`filled-${i}`}
              className={`cooldown-block cooldown-block-filled ${isPlanned && cooldownCurrent === 0 ? 'cooldown-block-planned' : ''}`}
            >
              <span className="cooldown-block-text">T</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default CooldownBlocks;
